/**
 * Exhaustive application of the v0 effect grammar (D15) — the only way
 * authored content changes `RuntimeState` (architecture.md §7.1 `effects/`).
 *
 * @remarks
 * Pure transitions: readonly state in, fresh state out, `never`-guarded
 * `switch` (architecture.md §3.5). `RandomnessState` rides INSIDE the state
 * that threads through every application, so the first drawn effect variant
 * slots into this signature without reshaping the pipeline — no v0 effect
 * draws, and the determinism suite pins `drawCount` untouched to prove it.
 *
 * Ordering is the author's: `executeTurn` folds an interaction's effects in
 * authored order, each application seeing its predecessor's state and
 * appending its transcript entries after its predecessor's.
 *
 * World-model notes ruled with the R5 plan: `add_item_to_inventory` also
 * marks the item removed-from-room (an item cannot be both placed and
 * carried); `remove_item` removes from wherever the item is — inventory
 * membership, placement, the drop record and the worn slot (D230) — because
 * it means "gone from the world";
 * `set_flag` on an undeclared flag is a corrupt bundle and throws.
 *
 * @packageDocumentation
 */

import type { ChooseEffect, CounterEffect, EffectDefinition, RuntimeState, SetEndingEffect } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandInHand } from '../conditions/command-in-hand.js';

import { requireCounter } from '../bundle/bundle-lookups.js';
import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { askPrompt } from '../conversation/prompts.js';
import { playerHealthOf, withPlayerHealth } from '../entities/actor-combat.js';
import { moveActor } from '../entities/actor-movement.js';
import { clampedToCounterRange, counterCrossingOf, counterValueOf, withCounterValue } from '../entities/counter-state.js';
import { withItemGranted, withItemRemoved, withMembership, withWorld } from '../entities/item-state.js';
import { takeCheckpoint } from '../execution/checkpoint.js';
import { EngineInvariantError } from '../execution/engine-invariant-error.js';
import { enterRoom } from '../execution/enter-room.js';
import { type StampedNarratives, stampNarrativeLines, type TranscriptCursor } from '../execution/transcript-cursor.js';
import { resolveNarrativeLines } from '../narrative/conditional-text.js';
import { terminalEndingOf } from '../narrative/session-ended.js';
import { speakVoiceLine } from '../narrative/speak-voice-line.js';

/** R9's flat tag for the lines of an effect a handler applies — an interaction's, a hook's. */
const HANDLER_PROVENANCE = 'effect:append_narrative';

/** The result of one effect application: advanced cursor, emitted entries, and the next state. */
export interface EffectApplication extends StampedNarratives {
	readonly state: RuntimeState;
}

/**
 * Apply one authored effect of any v0 variant.
 *
 * @param effect - The authored effect
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this effect
 * @param cursor - Where any emitted narration lands in the transcript
 * @param narrativeProvenance - R11: the `triggerContext` stamped on any
 *   `append_narrative` line — `event:<id>` from the tick, `trigger:<id>` from
 *   the watcher stage — so the transcript records WHO spoke, not just that an
 *   effect did. Defaults to the flat R9 tag for handler-applied effects.
 * @param commandInHand - D246: the sentence the list is answering, when it
 *   answers one (an authored verb's arm, a trigger listening to a command —
 *   the places the loader lets a condition read it): a branch's `when` and a
 *   line's variants read it as the arm's own conditions do
 * @returns The next state, any emitted entries, and the advanced cursor
 */
export const applyEffect = (
	effect: EffectDefinition,
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
	narrativeProvenance = HANDLER_PROVENANCE,
	commandInHand?: CommandInHand,
): EffectApplication => {
	// D233/D235: the two effects that act on a BODY — moving it, and having it
	// ask — share one arm, because both speak only to a player within sight or
	// earshot and both leave the rest of the grammar untouched.
	if (isBodyEffect(effect)) {
		return applyBodyEffect(effect, bundle, state, cursor);
	}

	// The quiet toggles — a door, a switch, the body's health — move one fact
	// and say nothing; one arm (split out at the complexity cap, D244).
	if (isQuietStateEffect(effect)) {
		return { cursor, entries: [], state: applyQuietStateEffect(effect, bundle, state) };
	}

	switch (effect.type) {
		case 'add_item_to_inventory': {
			return { cursor, entries: [], state: withItemGranted(state, effect.itemId) };
		}
		// D244: an amount moved, clamped — and, arriving at an edge, that edge's effects in the same pass.
		case 'adjust_counter':
		case 'set_counter': {
			return applyCounterEffect(effect, bundle, state, cursor);
		}
		case 'append_narrative': {
			// The effect IS a line (it spreads `narrativeLineFieldSchemas`) with a `type` on it — so everything a line
			// carries comes with it, its sounds (D264) included.
			const { type: _appendNarrative, ...line } = effect;
			const stamped = stampNarrativeLines(bundle, state, cursor, resolveNarrativeLines([line], bundle, state, commandInHand), narrativeProvenance);

			return { ...stamped, state };
		}
		/*
		 * R9: the clock's two hands. Scheduling stamps the occurrence with the
		 * turn being COMMITTED (`turnCount + 1` — effects run pre-commit) plus
		 * the delay, and takes the next sequence id so same-turn order is
		 * deterministic. Canceling removes every pending occurrence: a
		 * countdown averted is averted whole.
		 */
		case 'cancel_event':
		case 'schedule_event': {
			return { cursor, entries: [], state: applyClockEffect(effect, state) };
		}
		// D142: the photograph — see `execution/checkpoint.ts` for what a restore
		// keeps and what it resets.
		case 'checkpoint': {
			return { cursor, entries: [], state: takeCheckpoint(bundle, state, effect.wakeRoomId) };
		}
		// D246: the branch — the first holding arm's effects, folded as the list's own.
		case 'choose': {
			return applyChoose(effect, bundle, state, cursor, narrativeProvenance, commandInHand);
		}
		case 'move_player': {
			const entry = enterRoom(bundle, state, effect.targetRoomId, cursor);

			return { cursor: entry.cursor, entries: entry.entries, state: entry.state };
		}
		case 'remove_item': {
			return { cursor, entries: [], state: withItemRemoved(state, effect.itemId) };
		}
		/*
		 * R11: the story concludes. FIRST selection wins — a story ends once,
		 * so a later `set_ending` (a straggler event, a redundant trigger) is
		 * an idempotent no-op, not a re-conclusion. The ending's own epilogue
		 * lines are stamped `ending:<id>` so the transcript, the UI, and the
		 * replay suite can all see exactly which conclusion arrived and when.
		 */
		case 'set_ending': {
			return applySetEnding(effect, bundle, state, cursor);
		}
		case 'set_flag': {
			if (state.world.flagValues[effect.flagId] === undefined) {
				throw new EngineInvariantError(
					`set_flag references flag "${effect.flagId}" which is not in the runtime flag record — the loader's semantic gate should have rejected this bundle`,
				);
			}

			return {
				cursor,
				entries: [],
				state: {
					...state,
					world: {
						...state.world,
						flagValues: { ...state.world.flagValues, [effect.flagId]: effect.value },
					},
				},
			};
		}
		/*
		 * R12: the line's own policy decides — once, cooldown, gate conditions,
		 * pool cursor all live with the definition, and a declined speak is a
		 * silent pass-through (see narrative/speak-voice-line.ts).
		 */
		case 'speak_voice_line': {
			const speaking = speakVoiceLine(effect.voiceLineId, bundle, state, cursor);

			return { cursor: speaking.cursor, entries: speaking.entries, state: speaking.state };
		}
		case 'unlock_exit': {
			if (state.world.unlockedExitIds.includes(effect.exitId)) {
				return unchanged(state, cursor);
			}

			return {
				cursor,
				entries: [],
				state: {
					...state,
					world: {
						...state.world,
						unlockedExitIds: [...state.world.unlockedExitIds, effect.exitId],
					},
				},
			};
		}
	}
};

/**
 * D244: the counter pair — move the amount, clamped to the declared range,
 * and when the move ARRIVES at the floor or the ceiling, fold that edge's
 * `onMin` / `onMax` effects in the same pass.
 *
 * @remarks
 * The same pass is the point (Act II §13.1e): the tick that spends the
 * suit's last minute selects the death before the turn commits, so the
 * number on screen is the number of acts left, never one more. The edge's
 * lines stamp `counter_min:<id>` / `counter_max:<id>`, so the transcript
 * says which amount spoke. A move that changes nothing — already at the
 * floor and told to go lower — writes nothing and fires nothing. The
 * loader refuses an edge's effects that could cross another edge (a
 * crossing may not cause a crossing), so this fold always ends.
 *
 * @param effect - The adjustment or the setting
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this effect
 * @param cursor - Where an edge's lines land
 * @returns The application
 */
const applyCounterEffect = (effect: CounterEffect, bundle: EngineGameBundle, state: RuntimeState, cursor: TranscriptCursor): EffectApplication => {
	const counter = requireCounter(bundle, effect.counterId);
	const before = counterValueOf(bundle, state, effect.counterId);
	const after = clampedToCounterRange(counter, effect.type === 'adjust_counter' ? before + effect.by : effect.value);

	if (after === before) {
		return unchanged(state, cursor);
	}

	const moved = withCounterValue(state, effect.counterId, after);
	const crossing = counterCrossingOf(counter, before, after);

	if (crossing === undefined) {
		return { cursor, entries: [], state: moved };
	}

	// An edge is the amount's, not the sentence's: no command is in hand here, wherever the move stood.
	return applyEffects(crossing === 'min' ? counter.onMin : counter.onMax, bundle, moved, cursor, `counter_${crossing}:${effect.counterId}`);
};

/**
 * Fold an effect list in authored order — each application reading the
 * state its predecessor produced, its lines landing after its
 * predecessor's. The one fold every list in the engine goes through: a
 * handler's hook, a trigger, an event, a counter's edge, a branch's arm.
 *
 * @param effects - The list
 * @param bundle - The loaded bundle
 * @param state - The state before the first
 * @param cursor - Where the first line lands
 * @param narrativeProvenance - Whose lines these are (see {@link applyEffect})
 * @param commandInHand - The sentence being answered, if any (see {@link applyEffect})
 * @returns The state after the last, every entry, and the advanced cursor
 */
export const applyEffects = (
	effects: readonly EffectDefinition[],
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
	narrativeProvenance = HANDLER_PROVENANCE,
	commandInHand?: CommandInHand,
): EffectApplication =>
	effects.reduce<EffectApplication>(
		(folded, effect) => {
			const next = applyEffect(effect, bundle, folded.state, folded.cursor, narrativeProvenance, commandInHand);

			return { cursor: next.cursor, entries: [...folded.entries, ...next.entries], state: next.state };
		},
		{ cursor, entries: [], state },
	);

/**
 * D246: the branch. The first arm whose `when` holds — judged NOW, against
 * the state the list has reached (effects apply in order, so a `set_flag`
 * before the choose is seen by it), with the sentence in hand where there is
 * one — folds its effects exactly as the list around it folds its own: the
 * same provenance, the same command, and a counter moved inside an arm
 * crosses its edge in the same pass. An arm with no `when` is the
 * otherwise. No arm holding is nothing at all. Determinism holds by
 * construction: the arms are read in authored order against a state, and
 * nothing is drawn.
 *
 * @param effect - The branch
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE the branch
 * @param cursor - Where the arm's lines land
 * @param narrativeProvenance - Whose lines these are — the list's own
 * @param commandInHand - The sentence being answered, if any
 * @returns The application
 */
const applyChoose = (
	effect: ChooseEffect,
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
	narrativeProvenance: string,
	commandInHand: CommandInHand | undefined,
): EffectApplication => {
	const arm = effect.arms.find((candidate) => candidate.when === undefined || evaluateCondition(candidate.when, bundle, state, commandInHand));

	return applyEffects(arm?.effects ?? [], bundle, state, cursor, narrativeProvenance, commandInHand);
};

/** The effects that move one fact and say nothing — see {@link applyQuietStateEffect}. */
type QuietStateEffect = Extract<EffectDefinition, { type: (typeof QUIET_STATE_EFFECT_TYPES)[number] }>;

const QUIET_STATE_EFFECT_TYPES = ['close_container', 'open_container', 'restore_health', 'switch_off', 'switch_on'] as const satisfies readonly EffectDefinition['type'][];

/** Whether an effect is one of the quiet toggles — a guard, for the complexity cap's sake. */
const isQuietStateEffect = (effect: EffectDefinition): effect is QuietStateEffect => (QUIET_STATE_EFFECT_TYPES as readonly string[]).includes(effect.type);

/**
 * The quiet toggles, one arm each pair (D244 split, at the complexity cap).
 *
 * @remarks
 * D50: the door half of containment. Authored interactions are how most
 * things open in this game — the wall locker gives to a pry bar, not to
 * bare `open` — so an interaction has to be able to change the state a later
 * turn reads; exactly why `unlock_exit` exists on the exit side. D50/D56:
 * the two door verbs and the two switch verbs differ only in which set moves
 * and in which direction, so each pair is ONE arm rather than two
 * near-identical ones — a toggle written twice is a toggle whose halves can
 * drift. D244: the heal, what is left of the body restored toward the
 * manifest's whole.
 *
 * @param effect - The toggle
 * @param bundle - The loaded bundle
 * @param state - The state before
 * @returns The state with the one fact moved
 */
const applyQuietStateEffect = (effect: QuietStateEffect, bundle: EngineGameBundle, state: RuntimeState): RuntimeState => {
	switch (effect.type) {
		case 'close_container':
		case 'open_container': {
			return withWorld(state, {
				openedContainerItemIds: withMembership(state.world.openedContainerItemIds, effect.itemId, effect.type === 'open_container'),
			});
		}
		case 'restore_health': {
			return applyRestoreHealth(effect, bundle, state);
		}
		case 'switch_off':
		case 'switch_on': {
			return withWorld(state, { switchedOnItemIds: withMembership(state.world.switchedOnItemIds, effect.itemId, effect.type === 'switch_on') });
		}
	}
};

/** The two body effects, as one type — see {@link applyBodyEffect}. */
type BodyEffect = Extract<EffectDefinition, { type: 'ask_prompt' | 'move_actor' }>;

/** Whether an effect acts on a body — a guard rather than a disjunction, for the complexity cap's sake. */
const isBodyEffect = (effect: EffectDefinition): effect is BodyEffect => effect.type === 'move_actor' || effect.type === 'ask_prompt';

/**
 * The effects that act on a BODY (extracted at the complexity cap when D235
 * added the second): D233's move — the one way a body's location changes,
 * the delta record, the move spoken only to a player who sees it
 * (`entities/actor-movement.ts`) — and D235's ask, the general door to a
 * prompt: the actor asks because a trigger, a topic or a fall decided it
 * should. Like `speak_voice_line`, the ask owns its own policy (earshot,
 * spent, nothing offerable) and a declined ask is a silent pass-through
 * (`conversation/prompts.ts`).
 *
 * @param effect - The move or the ask
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this effect
 * @param cursor - Where any spoken line lands
 * @returns The application
 */
const applyBodyEffect = (
	effect: BodyEffect,
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
): EffectApplication => {
	const acted = effect.type === 'move_actor' ? moveActor(effect, bundle, state, cursor) : askPrompt(bundle, state, cursor, effect.actorId, effect.promptId);

	return { cursor: acted.cursor, entries: acted.entries, state: acted.state };
};

/** An application that changed nothing (idempotent effects re-applied). */
const unchanged = (state: RuntimeState, cursor: TranscriptCursor): EffectApplication => ({
	cursor,
	entries: [],
	state,
});

/**
 * `set_ending`, in full (extracted at the complexity cap) — first selection
 * wins; the ending's epilogue lines stamp `ending:<id>`.
 *
 * @param effect - The authored effect
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this effect
 * @param cursor - Where the epilogue lands in the transcript
 * @returns The application
 */
const applySetEnding = (
	effect: SetEndingEffect,
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
): EffectApplication => {
	const ending = bundle.indexes.endingsById.get(effect.endingId);

	if (ending === undefined) {
		throw new EngineInvariantError(
			`set_ending references ending "${effect.endingId}" which is not in the bundle — the loader's semantic gate should have rejected this bundle`,
		);
	}

	// D151 (Mike's ruling, from playing): an OPEN ending records that a thing
	// happened and leaves the door open behind it — D71 gave us the word to
	// say exactly that, and first-selection-wins was contradicting it. Only a
	// TERMINAL ending closes the slot. An ending already in the history is
	// not selected twice (it happened once); a second terminal is a defect in
	// the pack's composition that no static gate can see — refused LOUDLY.
	if (state.narrative.selectedEndingIds.includes(effect.endingId)) {
		return unchanged(state, cursor);
	}

	const closedBy = terminalEndingOf(bundle, state);

	if (closedBy !== undefined) {
		throw new EngineInvariantError(
			`set_ending selects "${effect.endingId}" after terminal ending "${closedBy.endingId}" closed the world — two terminal-path endings compose on one route; the pack's replay battery should have caught this (content-standards §5.2)`,
		);
	}

	const stamped = stampNarrativeLines(bundle, state, cursor, ending.narratives, `ending:${effect.endingId}`);

	return {
		...stamped,
		state: {
			...state,
			narrative: { ...state.narrative, selectedEndingIds: [...state.narrative.selectedEndingIds, effect.endingId]},
		},
	};
};

/**
 * R9: the clock's two hands, one arm. Scheduling stamps the occurrence with
 * the turn being COMMITTED (`turnCount + 1` — effects run pre-commit) plus the
 * delay, and takes the next sequence id so same-turn order is deterministic.
 * Canceling removes every pending occurrence: a countdown averted is averted
 * whole.
 *
 * @param effect - The clock effect
 * @param state - The state BEFORE this effect
 * @returns The state with its scheduler moved
 */
const applyClockEffect = (
	effect: Extract<EffectDefinition, { type: 'cancel_event' | 'schedule_event' }>,
	state: RuntimeState,
): RuntimeState => {
	if (effect.type === 'cancel_event') {
		return {
			...state,
			scheduler: {
				...state.scheduler,
				pendingEvents: state.scheduler.pendingEvents.filter((pendingEvent) => pendingEvent.eventDefinitionId !== effect.eventId),
			},
		};
	}

	return {
		...state,
		scheduler: {
			...state.scheduler,
			pendingEvents: [
				...state.scheduler.pendingEvents,
				{
					dueTurn: state.progression.turnCount + 1 + effect.delayTurns,
					eventDefinitionId: effect.eventId,
					sequenceId: state.scheduler.sequenceCounter,
				},
			],
			sequenceCounter: state.scheduler.sequenceCounter + 1,
		},
	};
};

/**
 * D244 (Act II §13.2): the heal. `to: 'full'` makes the player whole — the
 * delta leaves the record, as a player never struck carries none — and `by`
 * restores that much, never past the manifest's `player.health`. In a pack
 * whose player has no body there is nothing to restore (the loader refuses
 * such a heal; the engine leaves the world alone rather than invent one).
 *
 * @param effect - The heal
 * @param bundle - The loaded bundle (the body's whole)
 * @param state - The state before
 * @returns The state with the body restored
 */
const applyRestoreHealth = (effect: Extract<EffectDefinition, { type: 'restore_health' }>, bundle: EngineGameBundle, state: RuntimeState): RuntimeState => {
	const whole = bundle.contentPack.manifest.player?.health;
	const left = playerHealthOf(bundle, state);

	if (whole === undefined || left === undefined) {
		return state;
	}

	return withPlayerHealth(bundle, state, effect.to === 'full' || effect.by === undefined ? whole : Math.min(whole, left + effect.by));
};
